import { useState } from 'react';
import { notify } from '../stores/notificationStore';
import { logger } from '../utils/logger';
import { safeJsonParse, fetchWithAuth } from '../utils/api';

export interface CompanyData {
  name: string;
  address: string;
  city: string;
  postalCode: string;
  siret: string;
  tvaNumber: string;
  email?: string;        // Enrichi par Pappers
  phone?: string;        // Enrichi par Pappers
  website?: string;      // Enrichi par Pappers
  apeCode?: string;      // Code APE/NAF enrichi par Pappers
  enriched?: boolean;    // Indique si enrichi par Pappers
}

export interface VatVerificationResult {
  valid: boolean;
  countryCode: string;
  vatNumber: string;
  companyName?: string;
  companyAddress?: string;
  requestDate: Date;
  source: 'VIES' | 'CALCULATED';
  error?: string;
}

export const useSiretSearch = () => {
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<CompanyData[]>([]);

  /**
   * Rechercher par nom d'entreprise - Retourne plusieurs résultats
   * ✅ Utilise notre backend pour éviter les problèmes CORS
   */
  const searchByName = async (name: string): Promise<CompanyData[]> => {
    if (!name.trim() || name.trim().length < 2) {
      notify.warning('Veuillez entrer au moins 2 caractères', 'Nom trop court');
      return [];
    }

    setLoading(true);
    setResults([]);

    try {
      // ✅ Appel au backend (pas de CORS)
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001/api/v1';
      const response = await fetchWithAuth(
        `${apiUrl}/core/sirene/search?q=${encodeURIComponent(name.trim())}&per_page=10`
      );

      if (response.ok) {
        const result = await safeJsonParse(response);

        if (result.success && result.data?.results && result.data.results.length > 0) {
          const companies: CompanyData[] = result.data.results
            .filter((company: any) => company.siege?.siret) // Seulement ceux avec SIRET
            .map((company: any) => {
              const siret = company.siege.siret;
              const siren = siret.substring(0, 9);
              const tvaKey = (12 + 3 * (parseInt(siren) % 97)) % 97;
              const tvaNumber = `FR${tvaKey.toString().padStart(2, '0')}${siren}`;

              return {
                name: company.nom_complet || company.nom_raison_sociale || 'Société',
                address: company.siege?.adresse || '',
                city: company.siege?.libelle_commune || company.siege?.commune || '',
                postalCode: company.siege?.code_postal || '',
                siret,
                tvaNumber,
              };
            });

          setResults(companies);

          if (companies.length === 0) {
            notify.warning('Aucune société trouvée avec ce nom', 'Aucun résultat');
          } else {
            notify.success(`${companies.length} société(s) trouvée(s)`, 'Résultats');
          }

          return companies;
        }
      }

      notify.warning('Aucune société trouvée. Essayez un autre nom ou remplissez manuellement.', 'Aucun résultat');
      return [];
    } catch (error) {
      logger.error('Erreur recherche par nom:', error);
      notify.error('Erreur lors de la recherche. Veuillez réessayer.', 'Erreur de recherche');
      return [];
    } finally {
      setLoading(false);
    }
  };

  /**
   * Rechercher par SIRET - Retourne 1 seul résultat exact
   */
  const searchBySiret = async (siret: string): Promise<CompanyData | null> => {
    // Nettoyer le SIRET
    const cleanSiret = siret.replace(/\s/g, '');

    // Validation
    if (cleanSiret.length !== 14 || !/^\d+$/.test(cleanSiret)) {
      notify.warning('Le SIRET doit contenir exactement 14 chiffres', 'SIRET invalide');
      return null;
    }

    setLoading(true);
    try {
      // ✅ Appel au backend qui gère automatiquement: Pappers → INSEE → gouv.fr
      const data = await searchWithInsee(cleanSiret);
      if (data) {
        notify.success('Société trouvée ! Vérifiez et complétez les informations.', 'Société trouvée');
        return data;
      }

      notify.warning('Société non trouvée. Vérifiez le SIRET ou remplissez manuellement.', 'Société non trouvée');
      return null;
    } catch (error) {
      logger.error('Erreur recherche SIRET:', error);
      notify.error('Erreur lors de la recherche. Veuillez remplir manuellement.', 'Erreur de recherche');
      return null;
    } finally {
      setLoading(false);
    }
  };

  const searchWithInsee = async (siret: string): Promise<CompanyData | null> => {
    try {
      // ✅ Appel UNIQUEMENT au backend (proxy qui gère Pappers + INSEE + gouv.fr)
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001/api/v1';
      const backendResponse = await fetchWithAuth(`${apiUrl}/core/sirene/${siret}`);

      if (!backendResponse.ok) {
        logger.warn('Backend SIRENE search failed', { status: backendResponse.status });
        return null;
      }

      const result = await backendResponse.json();

      if (!result.success || !result.data) {
        logger.warn('Backend returned unsuccessful result', result);
        return null;
      }

      const data = result.data;

      // Si données viennent de Pappers (enrichies: email, phone, website)
      if (result.source === 'pappers' && result.enriched) {
        logger.info('✅ Données enrichies reçues depuis Pappers', {
          hasEmail: !!data.email,
          hasPhone: !!data.phone,
          hasWebsite: !!data.website,
        });

        return {
          name: data.name,
          address: data.address,
          city: data.city,
          postalCode: data.postalCode,
          siret: data.siret,
          tvaNumber: data.tvaNumber,
          email: data.email,
          phone: data.phone,
          website: data.website,
          apeCode: data.apeCode,
          enriched: true,
        };
      }

      // Si données viennent de l'INSEE (données de base uniquement)
      if (result.source === 'insee') {
        const etablissement = data.etablissement;
        const uniteLegale = etablissement.uniteLegale;
        const adresse = etablissement.adresseEtablissement;

        const siren = siret.substring(0, 9);
        const tvaKey = (12 + 3 * (parseInt(siren) % 97)) % 97;
        const tvaNumber = `FR${tvaKey.toString().padStart(2, '0')}${siren}`;

        const address = `${adresse.numeroVoieEtablissement || ''} ${adresse.typeVoieEtablissement || ''} ${adresse.libelleVoieEtablissement || ''}`.trim();
        const city = adresse.libelleCommuneEtablissement || adresse.libelleCommuneEtranger || '';

        return {
          name: uniteLegale.denominationUniteLegale ||
                `${uniteLegale.prenomUsuelUniteLegale || ''} ${uniteLegale.nomUniteLegale || ''}`.trim() ||
                'Société',
          address,
          city,
          postalCode: adresse.codePostalEtablissement || '',
          siret,
          tvaNumber,
          enriched: false,
        };
      }

      // Si données viennent de l'API gouv.fr (fallback)
      if (result.source === 'gouv' && result.data.results?.[0]) {
        const company = result.data.results[0];
        const siren = siret.substring(0, 9);
        const tvaKey = (12 + 3 * (parseInt(siren) % 97)) % 97;
        const tvaNumber = `FR${tvaKey.toString().padStart(2, '0')}${siren}`;

        return {
          name: company.nom_complet || company.nom_raison_sociale || 'Société',
          address: company.siege?.adresse || '',
          city: company.siege?.libelle_commune || company.siege?.commune || '',
          postalCode: company.siege?.code_postal || '',
          siret,
          tvaNumber,
          enriched: false,
        };
      }

      return null;
    } catch (error) {
      logger.error('Erreur recherche SIRET via backend:', error);
      return null;
    }
  };

  const clearResults = () => {
    setResults([]);
  };

  /**
   * Vérifier un numéro de TVA intracommunautaire via l'API VIES (UE)
   */
  const verifyVatNumber = async (vatNumber: string): Promise<VatVerificationResult | null> => {
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001/api/v1';
      const response = await fetchWithAuth(`${apiUrl}/core/sirene/verify-vat`, {
        method: 'POST',
        body: JSON.stringify({ vatNumber }),
      });

      if (response.ok) {
        const result = await safeJsonParse(response);
        if (result.success && result.data) {
          const data = result.data;

          // Afficher le résultat à l'utilisateur
          if (data.valid) {
            if (data.source === 'VIES') {
              notify.success(
                `✅ Numéro TVA vérifié auprès de l'UE (VIES)${data.companyName ? ` - ${data.companyName}` : ''}`,
                'TVA valide'
              );
            } else {
              notify.info(
                '✓ Numéro TVA valide (calculé selon formule DGFiP)',
                'TVA calculée'
              );
            }
          } else {
            notify.warning(
              data.error || 'Le numéro TVA semble invalide',
              'TVA non vérifiée'
            );
          }

          return data;
        }
      }

      notify.warning('Impossible de vérifier le numéro TVA pour le moment', 'Vérification TVA');
      return null;
    } catch (error) {
      logger.error('Erreur vérification TVA:', error);
      notify.error('Erreur lors de la vérification du numéro TVA', 'Erreur');
      return null;
    }
  };

  return {
    searchBySiret,
    searchByName,
    clearResults,
    verifyVatNumber,
    loading,
    results,
  };
};
